import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { SistemaRecurso, SomacylEntregablesDTO, StateInternalOrderSomacyl } from 'sigo-entities'

import { RootState } from '../../../AdapterStore'
import {
    useAddInventorySomacylOrdersMutation,
    useDeleteInventorySomacylOrdersMutation,
    useLazyGetByIdSomacylOrdersQuery,
    useLazyGetTransitSomacylOrdersQuery,
    useUploadPhotosSomacylOrdersMutation
} from '../../../slice'
import { indexedDB } from '../../../useIndexedDB'
import { IBodySomacyl } from '../../../../../Operaciones/OrdenesSomacyl/AddInventory'
import { helperAddInventory } from './helperAddInventory'
import { useLiveQuery } from 'dexie-react-hooks'
import { helperDeleteInventory } from './helperDeleteInventory'
import { helperUploadPhotos } from './helperUploadPhotos'
import { FTPService } from '../../../AdapterFileSystem'
import {
    initOfflineSyncOrdenesSomacylAdd,
    initOfflineSyncOrdenesSomacylDelete,
    initOfflineSyncOrdenesSomacylUploadPhotos
} from '../../../../../Operaciones/OrdenesSomacyl/syncManager'
import { AdapterGenerico } from '../../../AdapterGenerico'

export const useSomacylOrdersRepository = () => {
    const { user, personal } = useSelector((state: RootState) => state.auth)

    const [_getByIdSomacylOrdersApi, { isError, error }] = useLazyGetByIdSomacylOrdersQuery()
    const [
        _getTransitSomacylOrdersQuery,
        { isError: isErrorTransit, error: errorTransit }
    ] = useLazyGetTransitSomacylOrdersQuery()
    const [_addInventory] = useAddInventorySomacylOrdersMutation()
    const [_deleteInventory] = useDeleteInventorySomacylOrdersMutation()
    const [_uploadPhotosSomacylOrders] = useUploadPhotosSomacylOrdersMutation()

    const data = useLiveQuery(
        async () => await indexedDB.somacyl_orders
            .where('estado_interno')
            .equals(StateInternalOrderSomacyl.ASIGNADO)
            .toArray()
        , [],
        []
    )

    const [loading, setLoading] = useState<boolean>(false)

    const getByIdSomacylOrders = useCallback(async () => {
        setLoading(true)
        try {
            // ONLINE
            if (navigator.onLine) {
                let response = await _getByIdSomacylOrdersApi(user.Identificacion).unwrap()
                const now = Date.now()

                const pendingOrders = await indexedDB.somacyl_orders
                    .where('pendingSync')
                    .equals(1)
                    .toArray()

                let updateDelete = await initOfflineSyncOrdenesSomacylDelete(
                    response,
                    pendingOrders,
                    deleteInventory
                )

                response = await _getByIdSomacylOrdersApi(user.Identificacion).unwrap()

                let updateAdd = await initOfflineSyncOrdenesSomacylAdd(
                    response,
                    pendingOrders,
                    addInventory
                )

                let updateUploadPhotos = await initOfflineSyncOrdenesSomacylUploadPhotos(
                    response,
                    pendingOrders,
                    uploadPhotosSomacylOrders
                )

                if (updateDelete || updateAdd || updateUploadPhotos) {
                    await AdapterGenerico
                        .createMessage(
                            'Sincronizar',
                            `Se ha actualizado algunas ordenes, se va a actualizar la data.`,
                            'info'
                        )
                    response = await _getByIdSomacylOrdersApi(user.Identificacion).unwrap()
                }

                // Las órdenes que siguieron con pendingSync=1 después de los
                // intentos de arriba (p.ej. fotos que aún no están todas
                // disponibles en caché) NO se reemplazan por la respuesta del
                // servidor, porque esa respuesta todavía no refleja los cambios
                // locales pendientes: sobrescribirla borraría el entregables
                // (Filenames de fotos ya guardadas offline) sin haberlo logrado
                // subir, dejando al técnico con las fotos "desaparecidas".
                const stillPending = await indexedDB.somacyl_orders
                    .where('pendingSync')
                    .equals(1)
                    .primaryKeys()
                const stillPendingIds = new Set(stillPending)

                await indexedDB.somacyl_orders.bulkDelete(
                    (await indexedDB.somacyl_orders.toCollection().primaryKeys())
                        .filter((id) => !stillPendingIds.has(id))
                )
                await indexedDB.somacyl_orders.bulkPut(response
                    .filter((e) => !stillPendingIds.has(e._id))
                    .map(e => ({
                        ...e,
                        pendingSync: 0,
                        syncedAt: now,
                        updatedAt: now
                    })))

                return response
            }

            // OFFLINE
            const response = await indexedDB.somacyl_orders.toArray()
            return response
        } finally {
            setLoading(false)
        }

    }, [_getByIdSomacylOrdersApi])

    const getTransitSomacylOrdersQuery = useCallback(async (ID_Stock: number) => {
        setLoading(true)

        try {
            // ONLINE
            if (navigator.onLine) {
                const idrecurso = personal?.RecursosPersonal.find(e => e.Sistema === SistemaRecurso.SISTEMA_SOMACYL)
                if (!idrecurso) throw new Error(`¡No se pudo obtener id recurso!`)

                const response = await _getTransitSomacylOrdersQuery({ id_recurso: idrecurso.IdRecurso, codigo: ID_Stock }).unwrap()
                return response
            }

            // OFFLINE
            const localData = await indexedDB.somacyl_orders.toArray()

            if (!localData) return { transito: 0 }

            const transito = localData
                .flatMap(o => o.inventory)
                .filter(e => e.ID_Stock === ID_Stock)
                .reduce((sum, e) => sum + e.cantidad, 0)

            return { transito }
        } finally {
            setLoading(false)
        }
    }, [user, _getTransitSomacylOrdersQuery])

    const addInventory = useCallback(async (body: IBodySomacyl, id: string) => {
        setLoading(true)
        try {
            if (navigator.onLine) {
                const response = await _addInventory({ body, id }).unwrap()
                await indexedDB.somacyl_orders.put({
                    ...response,
                    pendingSync: 0,
                    syncedAt: Date.now(),
                    updatedAt: Date.now()
                })
            } else {
                const ownPersonal = user.ColeccionEmpresa[0].Propia
                await helperAddInventory(body, id, ownPersonal, getTransitSomacylOrdersQuery)
            }
        } finally {
            setLoading(false)
        }
    }, [_addInventory])

    const deleteInventory = useCallback(async (id: string, codigo: number, serie: string) => {
        setLoading(true)
        try {
            if (navigator.onLine) {
                const response = await _deleteInventory({ id, codigo, serie }).unwrap()
                await indexedDB.somacyl_orders.put({
                    ...response,
                    pendingSync: 0,
                    syncedAt: Date.now(),
                    updatedAt: Date.now()
                })
            } else {
                const ownPersonal = user.ColeccionEmpresa[0].Propia
                await helperDeleteInventory(id, codigo, serie, ownPersonal)
            }
        } finally {
            setLoading(false)
        }
    }, [_deleteInventory])

    const uploadPhotosSomacylOrders = useCallback(async (entregables: SomacylEntregablesDTO, id: string, files: File[]) => {
        setLoading(true)
        try {
            if (navigator.onLine) {
                // Se suben TODOS los archivos nuevos antes de tocar la entidad.
                // Si cualquiera falla, se interrumpe aquí y _uploadPhotosSomacylOrders
                // (que graba foto_antes/foto_despues/etc. en Mongo) nunca se ejecuta,
                // evitando que la entidad quede referenciando un Filename sin archivo
                // real en el servidor.
                for (const file of files) {
                    try {
                        await FTPService.setFileToServer(file.name, file)
                    } catch (error) {
                        throw new Error(`No se pudo subir el archivo "${file.name}": ${(error as Error).message}`)
                    }
                }
                const response = await _uploadPhotosSomacylOrders({ entregables, id }).unwrap()
                await indexedDB.somacyl_orders.put({
                    ...response,
                    pendingSync: 0,
                    syncedAt: Date.now(),
                    updatedAt: Date.now()
                })
                return
            }
            await helperUploadPhotos(entregables, id, files)
        } finally {
            setLoading(false)
        }
    }, [_uploadPhotosSomacylOrders])

    // AUTO LOAD
    // useEffect(() => {
    //     getByIdSomacylOrders()
    // }, [getByIdSomacylOrders])

    return {
        data,
        isLoading: loading,
        getByIdSomacylOrders,
        isError: isError || isErrorTransit,
        error: error || errorTransit,
        getTransitSomacylOrdersQuery,
        addInventory,
        deleteInventory,
        uploadPhotosSomacylOrders
    }
}